import CryptoJS from 'crypto-js';

// Shared secret agreed with the backend. Note this is the SECRET STRING, not a
// key in its own right — the AES key is its SHA-256 digest (see below).
const PAYLOAD_ENCRYPTION_KEY = import.meta.env.VITE_PAYLOAD_ENCRYPTION_KEY;

/**
 * Encrypts a single request field so the backend can decrypt it.
 *
 * Scheme (must match the backend exactly):
 * - Key:    SHA-256 digest of the secret string, giving 32 bytes for AES-256
 * - Cipher: AES-256-CBC with PKCS#7 padding
 * - IV:     16 random bytes, fresh per call
 * - Output: `base64(iv):base64(ciphertext)` — the backend splits on ':'
 *
 * Throws when the secret is missing rather than falling back to plaintext, so a
 * misconfigured environment fails loudly instead of leaking credentials.
 */
export function encryptField(plainText: string): string {
  if (!PAYLOAD_ENCRYPTION_KEY) {
    throw new Error(
      'VITE_PAYLOAD_ENCRYPTION_KEY is not set — cannot encrypt the login payload.'
    );
  }

  const key = CryptoJS.SHA256(PAYLOAD_ENCRYPTION_KEY);
  const iv = CryptoJS.lib.WordArray.random(16);

  const encrypted = CryptoJS.AES.encrypt(plainText, key, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  return `${iv.toString(CryptoJS.enc.Base64)}:${encrypted.ciphertext.toString(CryptoJS.enc.Base64)}`;
}
